import { boundMethod } from 'autobind-decorator';
import {
  AsyncStatus,
  LoadDescriptionT,
  UserPresence,
} from 'lib-shared-legacy-types';
import isEqual from 'lodash/isEqual';
import memoize from 'memoize-one';
import { ComponentType, PureComponent } from 'react';

import { ajaxSend } from 'api/api';
import { pubSub, PubSubSubscriptionDispose } from 'utils/pubSub';

import {
  presenceGetAll,
  PresenceGetAllResponse,
  presenceGetTotal,
  PresenceTotalResponse,
} from './api/presence';

const DEFAULT_FETCH_THROTTLE = 300;

export type PresenceFilter =
  | 'favourite'
  | 'online'
  | 'offline'
  | 'away'
  | 'all';

export interface WithPresenceInjectedProps {
  presence: {
    /** Loaded UserPresence of current page */
    items: Array<UserPresence>;

    /** Total amount of UserPresence with filters applied, page independant */
    total: number | null;

    /** Selected presence filter */
    filter: PresenceFilter;

    /** Filter by assigned only */
    isAssignedOnly: boolean;

    /** AsyncStatus of get all presence api */
    itemsAsyncStatus: AsyncStatus | null;

    /** AsyncStatus of get total presence api */
    totalAsyncStatus: AsyncStatus | null;

    /** The search term used in get UserPresence api */
    searchTerm: string;

    /** Whether there is next page or not */
    hasNextPage: boolean;

    /** Whether there is previous page or not */
    hasPreviousPage: boolean;

    /** Action to get presence. */
    getItems: () => Promise<PresenceGetAllResponse>;

    /** Get total presence */
    getTotal: () => Promise<PresenceTotalResponse>;

    /** Query items and total */
    queryItemsWithTotal: (
      favourites?: Array<string>,
    ) => Promise<[PresenceGetAllResponse, PresenceTotalResponse]>;

    /**
     * Handle search term change.
     *
     * @param {string} searchTerm - The search term
     * @param {number} [fetchThrottle=300] - If > 0 it will re-fetch presence
     *   after given time. Default is `300`
     */
    searchTermChange: (
      searchTerm?: string,
      fetchThrottle?: number,
    ) => Promise<[PresenceGetAllResponse, PresenceTotalResponse] | void>;

    /**
     * Handle filter change.
     *
     * @param {PresenceFilter} filter - Filter
     */
    filterChange: (
      filter: PresenceFilter,
    ) => Promise<[PresenceGetAllResponse, PresenceTotalResponse]> | void;

    /** Toggles assigned filter */
    assignedFilterChange(): Promise<
      [PresenceGetAllResponse, PresenceTotalResponse]
    >;

    /** Get presence of the next page */
    nextPage: () => Promise<PresenceGetAllResponse>;

    /** Get presence of the previous page */
    previousPage: () => Promise<PresenceGetAllResponse>;

    /** Provide actual favourites */
    provideFavourites: (favourites: Array<string>) => void;

    /** Provide search team */
    provideAssignedOnly: (isAssignedOnly: boolean) => void;

    /** Provide filter */
    provideFilter: (filter: PresenceFilter) => void;

    /** Provide search term */
    provideSearchTerm: (searchTerm: string) => void;
  };
}

type WithPresenceNotInjectedProps<T> = Omit<T, keyof WithPresenceInjectedProps>;

type WithPresenceProps<T> = WithPresenceNotInjectedProps<T>;

interface WithPresenceState {
  searchTerm: string;
  filter: PresenceFilter;
  isAssignedOnly: boolean;
  items: Array<UserPresence>;
  total: number | null;
  nextPagePath: string | null;
  previousPagePath: string | null;
  itemsAsyncStatus: AsyncStatus | null;
  totalAsyncStatus: AsyncStatus | null;
  favourites: Array<string>;
}

interface WithPresenceSettings {
  pageSize?: number;
}

const FILTER_MAP = {
  online: {
    status: 'online',
  },
  offline: {
    status: 'offline',
  },
  away: {
    status: 'away',
  },
};

export const withPresence = <T extends WithPresenceInjectedProps>(
  Component: ComponentType<T>,
  settings: WithPresenceSettings = {},
) => {
  class WithPresence extends PureComponent<
    WithPresenceProps<T>,
    WithPresenceState
  > {
    timeout: ReturnType<typeof setTimeout> | null;
    agentStatusChangedSubscription?: PubSubSubscriptionDispose;

    constructor(props: WithPresenceProps<T>) {
      super(props);

      this.timeout = null;
      this.state = {
        items: [],
        total: 0,
        isAssignedOnly: false,
        searchTerm: '',
        filter: 'all',
        nextPagePath: null,
        previousPagePath: null,
        itemsAsyncStatus: null,
        totalAsyncStatus: null,
        favourites: [],
      };
      this.memoizedGetInjectedProps = memoize(
        this.memoizedGetInjectedProps,
        isEqual,
      ).bind(this); // We have to bind it like this. Using @boundMethod won't work
    }

    componentDidMount() {
      this.agentStatusChangedSubscription = pubSub.subscribe(
        'agentStatusChanged',
        this.onPresenceAgentStatusChanged,
      );
    }

    componentWillUnmount() {
      if (this.timeout) {
        clearTimeout(this.timeout);
      }

      this.agentStatusChangedSubscription?.();
    }

    // Handle socket connection updates.
    @boundMethod
    onPresenceAgentStatusChanged(payload) {
      const userId = payload?.params?.user_id;
      const loadDescriptions = payload?.params?.presence?.load_descriptions;

      // payload doesn't include workloads_by_channel, so infer it from load_descriptions
      const workloads_by_channel = loadDescriptions?.reduce(
        (
          acc: Pick<UserPresence, 'workloads_by_channel'>,
          d: LoadDescriptionT,
        ) => ({
          ...acc,
          [d.conversation_channel]: [
            ...(acc[d.conversation_channel] || []),
            d.csid,
          ],
        }),
        {},
      );

      this.setState(({ items: prevItems }) => {
        const index = userId
          ? prevItems.findIndex((x) => x.agent_id === userId)
          : -1;
        if (index > -1) {
          const items = [...prevItems];
          const item = {
            ...prevItems[index],
            status: payload.params.new_status,
            connection_status: payload.params.new_status,
            simultaneous_ring: payload.params.presence.simultaneous_ringing,
            non_working_channels: payload.params.working_channels
              .filter((x) => !x.working)
              .map((x) => x.conversation_channel),
            workloads_by_channel,
          };
          items[index] = item;

          return {
            items,
          };
        }
        return null;
      });
    }

    @boundMethod
    queryItemsWithTotal(
      favourites?: Array<string>,
    ): Promise<[PresenceGetAllResponse, PresenceTotalResponse]> {
      return new Promise((resolve) => {
        const runResolver = () =>
          resolve(Promise.all([this.getItems(), this.getTotal()]));

        if (favourites) {
          this.setState({ favourites }, runResolver);
        } else {
          runResolver();
        }
      });
    }

    @boundMethod
    applyFilter(): Record<string, string | number> {
      const { filter, favourites, isAssignedOnly } = this.state;
      const queryFilter = { ...(FILTER_MAP[filter] || {}) };

      if (favourites.length > 0) {
        queryFilter.favourites = favourites.filter(Boolean).join(',');
      }

      if (isAssignedOnly) {
        queryFilter.has_assigned_conversations = true;
      }

      if (filter === 'favourite') {
        queryFilter.limit = favourites.length;
      }

      return queryFilter;
    }

    @boundMethod
    getItems(path?: string): Promise<PresenceGetAllResponse> {
      this.setState({ itemsAsyncStatus: 'loading' });

      const req = path
        ? ajaxSend<PresenceGetAllResponse>({ path })
        : presenceGetAll({
            search: this.state.searchTerm,
            limit: settings.pageSize,
            ...this.applyFilter(),
          });

      return req.then(this.handleQueryResolve).catch(this.handleQueryReject);
    }

    @boundMethod
    provideFavourites(favourites: Array<string>): void {
      this.setState({ favourites });
    }

    @boundMethod
    provideAssignedOnly(isAssignedOnly: boolean): void {
      this.setState({ isAssignedOnly });
    }

    @boundMethod
    provideFilter(filter: PresenceFilter): void {
      this.setState({ filter });
    }

    @boundMethod
    provideSearchTerm(searchTerm: string): void {
      this.setState({ searchTerm });
    }

    @boundMethod
    getTotal(): Promise<PresenceTotalResponse> {
      // If filter is `favourite` ignore /total and use limit value, since pagination is ignored in this case.
      if (this.state.filter === 'favourite') {
        const { limit } = this.applyFilter();
        const total = typeof limit === 'number' ? limit : 0;
        this.setState({ total });

        return Promise.resolve({
          data: {
            total,
          },
        });
      }

      this.setState({ totalAsyncStatus: 'loading' });

      return presenceGetTotal(this.applyFilter())
        .then((res: PresenceTotalResponse) => {
          this.setState({
            totalAsyncStatus: 'succeeded',
            total: res.data.total,
          });

          return res;
        })
        .catch((err) => {
          this.setState({ totalAsyncStatus: 'failed' });

          return err;
        });
    }

    @boundMethod
    nextPage(): Promise<PresenceGetAllResponse> | never {
      const { nextPagePath } = this.state;
      if (nextPagePath) {
        return this.getItems(nextPagePath);
      }
      throw new Error('Next page does not exist');
    }

    @boundMethod
    previousPage(): Promise<PresenceGetAllResponse> | never {
      const { previousPagePath } = this.state;
      if (previousPagePath) {
        return this.getItems(previousPagePath);
      }
      throw new Error('Previous page does not exist');
    }

    @boundMethod
    handleSearchTermChange(
      searchTerm = '',
      fetchThrottle: number = DEFAULT_FETCH_THROTTLE,
    ) {
      return new Promise<
        [PresenceGetAllResponse, PresenceTotalResponse] | void
      >((resolve) => {
        this.setState({ searchTerm }, () => {
          if (fetchThrottle >= 0) {
            if (this.timeout) {
              clearTimeout(this.timeout);
            }

            this.timeout = setTimeout(() => {
              resolve(this.queryItemsWithTotal());
            }, fetchThrottle);
          } else {
            resolve();
          }
        });
      });
    }

    @boundMethod
    handleAssignedFilterChange(): Promise<
      [PresenceGetAllResponse, PresenceTotalResponse]
    > {
      return new Promise((resolve) => {
        this.setState({ isAssignedOnly: !this.state.isAssignedOnly }, () => {
          resolve(this.queryItemsWithTotal());
        });
      });
    }

    @boundMethod
    handleFilterChange(
      filter: PresenceFilter,
    ): Promise<[PresenceGetAllResponse, PresenceTotalResponse]> {
      return new Promise((resolve) => {
        this.setState({ filter }, () => {
          resolve(this.queryItemsWithTotal());
        });
      });
    }

    @boundMethod
    handleQueryResolve(response: PresenceGetAllResponse) {
      this.setState(({ filter, total, totalAsyncStatus }) => {
        // If limit is 0 there're no items to fetch/show next.
        // favourite filter also replaces limit, hence hide next/prev pages.
        const isFavourite = filter === 'favourite';
        const nextPagePath =
          response.meta.next_page?.includes('limit=0') || isFavourite
            ? null
            : response.meta.next_page;
        const previousPagePath =
          response.meta.prev_page?.includes('limit=0') || isFavourite
            ? null
            : response.meta.prev_page;

        const items = this.filterFavourite(response.data.presence);
        const totalState: {
          total: number | null;
          totalAsyncStatus: AsyncStatus | null;
        } = isFavourite
          ? {
              total: items.length,
              totalAsyncStatus: 'succeeded',
            }
          : {
              total,
              totalAsyncStatus,
            };

        return {
          itemsAsyncStatus: 'succeeded',
          items,
          nextPagePath,
          previousPagePath,
          ...totalState,
        };
      });

      return response;
    }

    @boundMethod
    filterFavourite(items: Array<UserPresence>) {
      if (
        this.state.filter !== 'favourite' ||
        (!this.state.isAssignedOnly && !this.state.searchTerm)
      ) {
        return items;
      }

      // Backend does not filter out favourites but rather sorts it out to top applying limit.
      // Hence filter out here if search or isAssignedOnly is provided.
      return items.filter((x) => this.state.favourites.includes(x.agent_id));
    }

    @boundMethod
    handleQueryReject(error) {
      this.setState({
        itemsAsyncStatus: 'failed',
      });

      return error;
    }

    @boundMethod
    getInjectedProps(
      props: Readonly<WithPresenceProps<T>>,
      state: Readonly<WithPresenceState>,
    ): WithPresenceInjectedProps['presence'] {
      const {
        items,
        total,
        isAssignedOnly,
        searchTerm,
        filter,
        nextPagePath,
        previousPagePath,
        itemsAsyncStatus,
        totalAsyncStatus,
      } = state;

      return this.memoizedGetInjectedProps(
        searchTerm,
        items,
        total,
        isAssignedOnly,
        filter,
        itemsAsyncStatus,
        totalAsyncStatus,
        nextPagePath,
        previousPagePath,
      );
    }

    memoizedGetInjectedProps(
      searchTerm: Readonly<WithPresenceState>['searchTerm'],
      items: Readonly<WithPresenceState>['items'],
      total: Readonly<WithPresenceState>['total'],
      isAssignedOnly: Readonly<WithPresenceState>['isAssignedOnly'],
      filter: Readonly<WithPresenceState>['filter'],
      itemsAsyncStatus: Readonly<WithPresenceState>['itemsAsyncStatus'],
      totalAsyncStatus: Readonly<WithPresenceState>['totalAsyncStatus'],
      nextPagePath: Readonly<WithPresenceState>['nextPagePath'],
      previousPagePath: Readonly<WithPresenceState>['previousPagePath'],
    ): WithPresenceInjectedProps['presence'] {
      return {
        searchTerm,
        items,
        total,
        filter,
        isAssignedOnly,
        itemsAsyncStatus,
        totalAsyncStatus,
        hasNextPage: !!nextPagePath,
        hasPreviousPage: !!previousPagePath,
        getItems: this.getItems,
        getTotal: this.getTotal,
        searchTermChange: this.handleSearchTermChange,
        filterChange: this.handleFilterChange,
        nextPage: this.nextPage,
        previousPage: this.previousPage,
        queryItemsWithTotal: this.queryItemsWithTotal,
        assignedFilterChange: this.handleAssignedFilterChange,
        provideFavourites: this.provideFavourites,
        provideFilter: this.provideFilter,
        provideAssignedOnly: this.provideAssignedOnly,
        provideSearchTerm: this.provideSearchTerm,
      };
    }

    render() {
      return (
        <Component
          {...(this.props as T)}
          presence={this.getInjectedProps(this.props, this.state)}
        />
      );
    }
  }

  return WithPresence;
};
